import api from '@/http/api';
import { cn, stripHtml } from '@/lib/utils';
import { useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuthStore } from '@/store';
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import { format } from 'date-fns';
import { Check, CheckCheck, Search, X, ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Discussion from '../OrderDetail/Discussion';
import { labelStyle, parseLabels } from '@univdiam/ui/lib/tagColors';
import { useOrderTags } from '@/sharedData/OrderTags';

import { useSocketStore } from '@/socketStore';

const getAllDiscussions = (page: number, search: string, unreadOnly: boolean) => {
  return api.get(`/admin/discussion/all-discussions?page=${page}&limit=20&search=${encodeURIComponent(search)}${unreadOnly ? '&unread=1' : ''}`);
};

type DiscussionData = {
  "customer_id": string,
  "customer_name": string,
  "business_name"?: string,
  "order_id": string,
  "order_ref": string,
  "job_title": string
  "labels"?: string[] | string,
  "last_message": string,
  "last_chat_date": string
  "is_read": number
  "unread_count": number
}

function AllDiscussion() {
  const queryClient = useQueryClient();
  const sendNotification = useSocketStore((s) => s.sendNotification);
  const { data: orderTagsData } = useOrderTags();
  const adminTags = orderTagsData ?? [];

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Filtered server-side: the list is paged, so filtering what is loaded would
  // miss unread discussions beyond the first page.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const discussionUnreadCount = useAuthStore((s) => s.discussionUnreadCount);

  // Debounce keystrokes into the query that actually hits the server.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = useInfiniteQuery({
    queryKey: ["all-admin-discussions", searchQuery, unreadOnly],
    queryFn: async ({ pageParam = 1 }) => {
      return await getAllDiscussions(pageParam as number, searchQuery, unreadOnly);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { page, totalPages } = lastPage.data.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    // Keep the old results on screen while a new search fetches, instead of
    // blanking the list on every keystroke.
    placeholderData: keepPreviousData,
  });
  
  const discussions = data?.pages.flatMap((page: any) => page.data.data.orderDiscussion) as DiscussionData[] || [];
  const [selectedDiscussion, setSelectedDiscussion] = useState<DiscussionData | null>(null)
  const { user, setNotificationCount } = useAuthStore();

  const handleSelectDiscussion = (discussionItem: DiscussionData) => {
    setSelectedDiscussion(discussionItem);
    
    // Clear the unread count in frontend immediately for optimistic UI
    if (discussionItem.unread_count > 0) {
      // Clear in main query
      queryClient.setQueryData(["all-admin-discussions", searchQuery, unreadOnly], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              data: {
                ...page.data.data,
                orderDiscussion: page.data.data.orderDiscussion.map((d: any) =>
                  String(d.order_id) === String(discussionItem.order_id)
                    ? { ...d, unread_count: 0 }
                    : d
                )
              }
            }
          }))
        };
      });

      api.patch(`/admin/discussion/read-all/${discussionItem.order_id}`)
        .then((res) => {
          queryClient.invalidateQueries({ queryKey: ["all-admin-discussions"] });
          // Holds a NUMBER of discussions with unread, so refetch rather than patch.
          queryClient.invalidateQueries({ queryKey: ["sidebar-discussion-unread"] });
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
          if (res.data?.unseenCount !== undefined) {
             setNotificationCount(res.data.unseenCount);
             sendNotification({
               action_by: user?.role!,
               action_by_id: user?.id.toString()!,
               action_type: 'discussion_read',
               order_id: discussionItem.order_id,
               title: `Discussion Read Receipt`,
               metadata: {
                 order_ref: discussionItem.order_ref,
               },
             });
          }
        })
        .catch(err => {
          console.error("Failed to mark all messages as read:", err);
        });
    }
  };

  return (
    <div className="p-3 lg:p-6 bg-card text-card-foreground flex h-full w-full flex-col lg:flex-row gap-6">
      <div className={cn("bg-card text-card-foreground flex h-full min-h-0 flex-col gap-6 rounded-xl border py-6 w-full pb-0 lg:w-96 shrink-0", selectedDiscussion && 'hidden lg:flex')}>
        {/* Header */}
        <div className="grid auto-rows-min grid-cols-[1fr_auto] items-start gap-1.5 px-6">
          <h2 className="font-display font-semibold text-xl lg:text-2xl">Discussions</h2>
        </div>

        {/* Search */}
        <div className="relative px-6">
          <Search
            width="24"
            height="24"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-search text-muted-foreground absolute left-10 top-1/2 size-4 -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 ps-10 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
          {isPlaceholderData ? (
            <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
          ) : (
            searchInput !== '' &&
            <X className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-pointer" onClick={() => {
              setSearchInput('');
            }} />
          )}
        </div>

        {/* Filter */}
        <div className="-mt-3 flex gap-1.5 px-6">
          {([["All", false], ["Unread", true]] as const).map(([label, value]) => (
            <button
              key={label}
              type="button"
              aria-pressed={unreadOnly === value}
              onClick={() => setUnreadOnly(value)}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors cursor-pointer",
                unreadOnly === value ? "bg-[#883734]/10 text-[#883734]" : "text-muted-foreground hover:bg-muted"
              )}
            >
              {label}
              {value && discussionUnreadCount > 0 && <span className="ms-1 opacity-70">{discussionUnreadCount}</span>}
            </button>
          ))}
        </div>

        {/* Chat List */}
        <div className="flex-1 min-h-0 overflow-auto p-0" onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          if (scrollHeight - scrollTop <= clientHeight + 50) {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }
        }}>
          <div className="block min-w-0 divide-y">
            {discussions?.map((discussionItem) => (
              <div
                key={discussionItem.order_id}
                className={cn("group/item hover:bg-muted relative flex min-w-0 cursor-pointer items-start gap-4 px-6 py-4", discussionItem.order_id === selectedDiscussion?.order_id && 'bg-[#883734]/10')}
                onClick={() => handleSelectDiscussion(discussionItem)}
              >

                {/* Discussion Info */}
                <div className="min-w-0 grow space-y-1">
                  {/* Row 1: Order Name (primary) + timestamp */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-foreground">
                      {discussionItem.job_title}
                    </span>
                    {
                      discussionItem.last_chat_date && <span className="text-muted-foreground flex-none text-[11px]">
                        {format(new Date(discussionItem.last_chat_date!), 'dd MMM')} • {formatTimeInUserTimezone(discussionItem.last_chat_date!)}
                      </span>
                    }
                  </div>

                  {/* Row 2: Customer business name + order ref badge */}
                  <div className="flex items-center gap-2">
                    {(discussionItem.business_name || discussionItem.customer_name) && (
                      <span className="truncate text-xs font-medium text-foreground/80">
                        {discussionItem.business_name || discussionItem.customer_name}
                      </span>
                    )}
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      #{discussionItem.order_ref}
                    </span>
                  </div>

                  {/* Row 2b: Order labels */}
                  {parseLabels(discussionItem.labels).length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {parseLabels(discussionItem.labels).slice(0, 3).map((label) => (
                        <span
                          key={label}
                          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none"
                          style={labelStyle(label, adminTags)}
                        >
                          {label}
                        </span>
                      ))}
                      {parseLabels(discussionItem.labels).length > 3 && (
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          +{parseLabels(discussionItem.labels).length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Row 3: Last message + unread badge */}
                  {
                    discussionItem.last_chat_date && <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                      {
                        discussionItem.is_read ? <CheckCheck
                          strokeWidth={2}
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-check-check h-3.5 w-3.5 shrink-0 text-green-500"
                          size={24}
                        /> : <Check
                          width="24"
                          height="24"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-check text-muted-foreground h-3.5 w-3.5 shrink-0"
                        />
                      }

                      <span className="text-muted-foreground truncate text-start text-xs flex-1 min-w-0">
                         {stripHtml(discussionItem.last_message) || "Attachment"}
                      </span>

                      {discussionItem.unread_count > 0 && (
                        <div className="ms-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500 text-[10px] font-medium text-white">
                          {discussionItem.unread_count}
                        </div>
                      )}
                    </div>
                  }

                </div>
              </div>
            ))}
            {discussions?.length === 0 && (
              <div className="px-6 py-8 text-center text-muted-foreground text-sm">
                {unreadOnly ? "No unread discussions" : "No discussions found."}
              </div>
            )}
            {isFetchingNextPage && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>}
            {!hasNextPage && discussions?.length > 0 && (
              <div className="p-4 flex justify-center text-xs text-muted-foreground">
                No more discussions
              </div>
            )}
          </div>
        </div>

      </div>
      <div className={cn("h-full w-full min-h-0 min-w-0 flex-1", !selectedDiscussion && 'hidden lg:block')}>
        {
          selectedDiscussion ? <div className='h-full flex flex-col min-h-0'>
            {/* Mobile Back Button */}
            <div className="lg:hidden flex items-center mb-3">
              <button onClick={() => setSelectedDiscussion(null)} className="flex items-center text-sm font-medium hover:bg-muted p-2 rounded-md">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to discussions
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <Discussion
                orderRefProp={selectedDiscussion.order_ref}
                orderIdProp={selectedDiscussion.order_id}
                customerIdProp={selectedDiscussion.customer_id}
                headerActions={
                  <>
                    {parseLabels(selectedDiscussion.labels).map((label) => (
                      <span
                        key={label}
                        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none"
                        style={labelStyle(label, adminTags)}
                      >
                        {label}
                      </span>
                    ))}
                    <Link
                      to={`/order/${selectedDiscussion.order_id}/${selectedDiscussion.order_ref}/${selectedDiscussion.customer_id}`}
                      className="font-semibold text-lg flex items-center justify-center gap-1 rounded border border-transparent bg-gradient-to-b from-white/10 to-white/0 px-2 py-1 shadow-[0_0_0_0.35px_rgba(22,17,17,0.12),0_0.71px_1.77px_rgba(22,17,17,0.24)] transition-colors hover:bg-gray-100 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="text-[10px] font-medium text-black">View order</span>
                    </Link>
                  </>
                }
              />
            </div>
          </div> : <p className="text-xl w-full h-full flex items-center justify-center bg-white rounded-xl border">Please select a discussion</p>
        }
      </div>
    </div>

  );
}

export default AllDiscussion;
